import useDeleteFile from 'hooks/query/s3/useDeleteFile'
import { useUploadVideo } from 'hooks/query/s3/useUploadFile'
import { LocalImageUpload } from 'pages/content-video-editor/components/ImageUploadBox/local-image-upload'
import React, {
  CSSProperties,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-toastify'
import { isMobile } from 'react-device-detect'
import { UseFormSetValue, UseFormWatch } from 'react-hook-form'

import { FormValues } from 'utils/type'
import classNames from 'classnames'
import { ReactComponent as TrashIcon } from 'svg/fi_trash-2.svg'
import { PRODUCT_THUMBNAIL_IMAGE_SLOT_CNT } from 'utils/const'
import { VideoPlayer } from './VideoPlayer'
import { Loading } from './Loading'
import { CreateVideoType } from 'constant/videoTypes'
interface VideoFileUploaderProps {
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  index: number
  setSelectedVideos: Dispatch<
    SetStateAction<{ order: number; videoUrl: string }[]>
  >
  selectedVideos: { order: number; videoUrl: string }[]
  createVideoType: CreateVideoType
}
export const VideoFileUploader = ({
  watch,
  index,
  setValue,
  setSelectedVideos,
  selectedVideos,
  createVideoType,
}: PropsWithChildren<VideoFileUploaderProps>) => {
  const [isImageDivOpen, setImageDivOpen] = useState<boolean>(false)

  const handleImageDivOpen = () => {
    setImageDivOpen((prev) => !prev)
  }
  const fileInputRef = useRef<(HTMLInputElement | null)[]>([])
  const [selectedOrder, setSelectedOrder] = useState<number[]>([])
  const [images, setImages] = useState<string[]>([])
  const [selectedUploadImages, setSelectedUploadImages] = useState(
    new Array(images.length).fill(true),
  )

  const [uploadVideos, setUploadVideos] = useState<
    {
      id: number
      file: File
      preview: string
      uploading: boolean
    }[]
  >([])

  const { mutateAsync: uploadFileMutate, isPending } = useUploadVideo()
  const { mutateAsync: deleteMutate } = useDeleteFile()

  const handleCheckboxChange = (index: number) => {
    const uploadImgCnt = uploadVideos.length ?? 0
    const loadImageCnt = selectedOrder.length ?? 0
    const isAdd = 8 - uploadImgCnt - loadImageCnt > 0

    setSelectedOrder((prev) => {
      if (prev.includes(index)) {
        // 선택 해제: 제거 + 순서 보정
        return prev.filter((i) => i !== index)
      } else if (isAdd) {
        // 새 선택: 뒤에 추가
        return [...prev, index]
      } else {
        return prev
      }
    })
  }

  const handleButtonClick = (refIndex: number) => {
    fileInputRef.current[refIndex]?.click()
  }

  const updateVideos = (videoUrl: string) => {
    const found = selectedVideos.find((video) => video.videoUrl === videoUrl)

    if (found) {
      // 선택 해제
      const filtered = selectedVideos
        .filter((video) => video.videoUrl !== videoUrl)
        .map((video, idx) => ({ ...video, order: idx + 1 }))

      setSelectedVideos(filtered)
      setValue(
        `products.${index}.selectedVideos`,
        filtered.map((i) => i.videoUrl),
      )
    } else {
      const updated = [
        ...selectedVideos,
        { order: selectedVideos.length + 1, videoUrl: videoUrl },
      ]
      setSelectedVideos(updated)

      setValue(
        `products.${index}.selectedVideos`,
        updated.map((i) => i.videoUrl),
      )
    }
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files) return
    const MAX_FILE_SIZE_MB = 20
    const selectedFiles = Array.from(event.target.files)
    const originalFile = selectedFiles[0]

    // 파일 확장자 검증
    const allowedExtensions = ['.mp4', '.avi', '.mov']
    const fileExtension = originalFile.name
      .toLowerCase()
      .slice(originalFile.name.lastIndexOf('.'))

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error(
        `❌ ${originalFile.name}은(는) 지원하지 않는 파일 형식입니다. mp4, avi, mov 파일만 업로드 가능합니다.`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        },
      )
      return
    }

    // MIME 타입 검증
    const allowedMimeTypes = ['video/mp4', 'video/avi', 'video/quicktime']
    if (!allowedMimeTypes.includes(originalFile.type)) {
      toast.error(
        `❌ ${originalFile.name}은(는) 올바른 비디오 파일이 아닙니다.`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        },
      )
      return
    }

    const fileSizeMB = originalFile.size / (1024 * 1024)
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error(
        `❌ ${originalFile.name}은(는) ${MAX_FILE_SIZE_MB}MB 초과 파일입니다.`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        },
      )
      return
    }

    const tempId = Date.now() + Math.random()

    setUploadVideos((prev) => [
      ...prev,
      {
        file: originalFile,
        preview: '',
        uploading: true,
        id: tempId,
      },
    ])

    try {
      const { fileUrl } = await uploadFileMutate({
        file: originalFile,
        type: 'video',
      })

      setUploadVideos((prev) =>
        prev.map((item) =>
          item.id === tempId
            ? {
                ...item,
                preview: fileUrl,
                uploading: false,
              }
            : item,
        ),
      )
      updateVideos(fileUrl)
    } catch (error) {
      toast.error(
        `❌ ${originalFile.name}은(는) upload에 실패했습니다. 다시 시도해주세요!`,
        {
          position: 'bottom-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
        },
      )
      console.log(error)
    } finally {
      event.target.value = ''
    }
  }
  const getFileKey = (file: File) =>
    `${file.name}_${file.size}_${file.lastModified}`
  const handleDeleteFile = (targetFile: File, targetUrl: string) => {
    console.log(targetUrl, 'targetUrl')
    const targetKey = getFileKey(targetFile)
    if (targetUrl) {
      deleteMutate(targetUrl)
    }
    setUploadVideos((prev) =>
      prev.filter((imgObj) => imgObj.preview !== targetUrl),
    )
    setSelectedUploadImages((prev) => {
      const targetIndex = uploadVideos.findIndex(
        (imgObj) => getFileKey(imgObj.file) === targetKey,
      )
      if (targetIndex === -1) return prev
      const newSelected = [...prev]
      newSelected.splice(targetIndex, 1)
      return newSelected
    })

    // 전체 썸네일 이미지 파일을 관리하는 순서를 정리해줘야한다
    const filtered = selectedVideos
      .filter((img) => img.videoUrl !== targetUrl)
      .map((img, idx) => ({ ...img, order: idx + 1 }))

    setSelectedVideos(filtered)
    setValue(
      `products.${index}.selectedVideos`,
      filtered.map((i) => i.videoUrl),
    )
  }

  return (
    <>
      {/* <div className="flex justify-between md:w-full w-[343px]  bg-[#EEF1F7] items-center px-5 py-[13px] rounded-[10px] mt-2">
        <p className="font-bold text-[14px]">
          [선택] 이미지 업로드{' '}
          {
            watch(`products.${i}.selectedFiles`, []).filter((image) => image)
              .length
          }
          /8
          <span className="font-[400] ml-3">5MB | jpg, png</span>
        </p>
        <button
          className="flex items-center justify-center gap-[8px] w-[65px] h-[44px] md:text-base text-[14px] "
          type="button"
          onClick={() => {
            handleImageDivOpen()
          }}
        >
          <span>{isImageDivOpen ? '접기' : '펼치기'}</span>
          <img
            src="assets/img/icon_up_arrow.png"
            alt="arrow"
            className={`w-[12.8px] h-[8.25px]  transition-transform duration-500 ${
              isImageDivOpen ? '' : 'rotate-180'
            }`}
          />
        </button>
      </div> */}
      {/* <CSSTransition
        in={isImageDivOpen}
        timeout={200}
        classNames="content"
        unmountOnExit
      > */}
      <div
        className="mt-2"
        style={{
          width: '100%',
          background: '#F6F8FB',
          borderRadius: '10px',
          padding: '20px',
          maxHeight: 'calc(2 * 230px)', // 2줄까지만 보이도록 높이 제한 (2줄 높이 + 갭)
          overflowY: 'auto', // 스크롤 가능하게 설정
          scrollbarWidth: 'thin', // Firefox용 기본 스크롤바 폭 설정
          scrollbarColor: '#888 #e0e0e0', // Firefox용 스크롤바 색상 설정
        }}
      >
        <div>
          <p className="mb-4 text-[14px] font-bold">
            [선택] 영상 업로드 {uploadVideos.length}
            /8
            <span className="ml-3 font-[400]">40초 | 10MB | mp4,avi,mov</span>
          </p>
          <p className="text-[14px]">
            필수 입력 사항이 아닙니다. 상품 페이지 대표 이미지 외에{' '}
            <span className="font-bold text-error">
              따로 영상 추가를 원하시는 경우에만{' '}
            </span>
            업로드 해주세요. <br />
            대본 길이에 따라 선택한 영상이 잘리거나 사용되지 않을 수 있습니다.
          </p>
        </div>
        <div className="mt-4 grid min-h-[110px] grid-cols-3 gap-y-2 md:grid-cols-8">
          {uploadVideos.map((el, index) => {
            const selected = selectedVideos.find(
              (img) => img.videoUrl === el.preview,
            )
            const order = selected?.order ?? 0

            return (
              <div
                className={classNames('shrink-0 bg-[#F6F8FB]', {
                  'relative flex w-[85px] flex-col items-center justify-center overflow-hidden rounded-[4px]':
                    isMobile,
                  'relative flex w-[86px] flex-col items-center justify-center overflow-hidden rounded-[4px]':
                    !isMobile,
                  'min-h-[151px]':
                    createVideoType === CreateVideoType.PRODUCT_SHORT,
                  'h-[85px]': createVideoType !== CreateVideoType.PRODUCT_SHORT,
                })}
                key={index}
              >
                {' '}
                {el.uploading && <Loading />}
                <div className="absolute right-1 top-1 z-50 flex cursor-pointer items-center active:scale-95">
                  <div
                    className={
                      'flex h-[28px] w-[28px] items-center justify-center rounded-full border-[1px] bg-[#2E343D]'
                    }
                  >
                    <button
                      className="z-50 flex h-[24px] w-[24px] items-center justify-center rounded-full bg-[#2E343D] font-bold text-white"
                      type="button"
                      onClick={() => {
                        handleDeleteFile(el.file, el.preview)
                      }}
                    >
                      <TrashIcon className="z-50" />
                    </button>
                  </div>
                </div>
                <div
                  className="absolute bottom-6 right-1 z-50 flex cursor-pointer items-center active:scale-95"
                  onClick={() => {
                    updateVideos(el.preview)
                  }}
                >
                  <div
                    className={classNames(
                      'flex h-[28px] w-[28px] items-center justify-center rounded-full border-2 border-href',
                      {
                        'bg-white opacity-100': order > 0,
                        'border-white bg-[rgba(0,0,0,0.5)]': order <= 0,
                      },
                    )}
                  >
                    {order > 0 && (
                      <div className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-href font-bold text-white">
                        {order}
                      </div>
                    )}
                  </div>
                </div>
                <VideoPlayer
                  path={el.preview}
                  onClick={() => {
                    updateVideos(el.preview)
                  }}
                  createVideoType={createVideoType}
                />
              </div>
            )
          })}
          {new Array(
            PRODUCT_THUMBNAIL_IMAGE_SLOT_CNT -
              selectedOrder.length -
              (uploadVideos.length ?? 0),
          )
            .fill(1)
            .map((src, index) => (
              <div key={index} className="cursor-pointer">
                <div className="">
                  <LocalImageUpload
                    handleButtonClick={() => {
                      handleButtonClick(index)
                    }}
                    label="영상"
                    index={index}
                    isPending={isPending}
                    className={classNames('', {
                      'flex h-[85px] w-[85px] flex-col items-center justify-center gap-[10px] rounded-[4px] bg-white text-[#8F929B] hover:bg-gray-200':
                        createVideoType !== CreateVideoType.PRODUCT_SHORT,
                      'flex h-[151px] w-[85px] flex-col items-center justify-center gap-[10px] rounded-[4px] bg-white text-[#8F929B] hover:bg-gray-200':
                        createVideoType === CreateVideoType.PRODUCT_SHORT,
                    })}
                  />
                  <input
                    ref={(el) => (fileInputRef.current[index] = el)}
                    id={`file-input-${index}`}
                    type="file"
                    accept=".avi, .mov, .mp4"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
      {/* </CSSTransition> */}
    </>
  )
}
